import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import DailyBriefing from "@/components/DailyBriefing";
import {
  FlaskConical, Wrench, FileText, FlaskRound, ClipboardCheck, ArrowUpRight, Lock, PlugZap,
  Presentation, Download, Sparkles, Mail, Microscope, Activity, ScanText,
} from "lucide-react";

const MODULES = [
  {
    key: "case-tracking",
    name: "PathTraX — Case Tracking",
    icon: FlaskConical,
    blurb: "Accession cases, follow specimens across the bench, cut blocks and slides, request recuts and sign out final reports.",
    to: "/cases",
    links: [
      ["Cases", "/cases"], ["Lab Dashboard", "/dashboard"], ["My Worklist", "/my-worklist"],
      ["Bench Worklist", "/tech-worklist"], ["Patient History", "/patients"],
      ["Blocks & Slides", "/inventory"], ["Histology Requests", "/histology-requests"], ["Bulk Slides", "/bulk-slides"],
      ["Send-Outs & Billing", "/sendouts"], ["Billing Worklist", "/worklist"],
      ["Templates", "/templates"], ["Statistics", "/statistics"],
      ["Transcription", "/transcription"],
    ],
  },
  {
    key: "assistant",
    name: "AI Assistant",
    icon: Sparkles,
    blurb: "Ask about your cases in plain English or out loud, hear the answer spoken back, and see live alerts for turnaround breaches, incomplete cases and workflow bottlenecks.",
    to: "/assistant",
    perm: "assistant",
    links: [["AI Assistant", "/assistant"], ["Scan Mode", "/scan"], ["Lab Dashboard", "/dashboard"]],
  },
  {
    key: "equipment",
    name: "TechTraX — Equipment Maintenance",
    icon: Wrench,
    blurb: "Instrument register, PM schedules with a bench checklist, service and calibration history, reagent changes and temperature logs.",
    to: "/equipment",
  },
  {
    key: "documents",
    name: "PolicyTraX — SOP & Document Manager",
    icon: FileText,
    blurb: "Controlled SOPs with permanent control numbers, revision history, two-step approval and read-and-understood tracking.",
    to: "/documents",
  },
  {
    key: "reagents",
    name: "ChemTraX — Reagent Management",
    icon: FlaskRound,
    blurb: "Lot and expiry tracking, QC records with automatic quarantine, manufacturer details, SDS library and low-stock alerts.",
    to: "/reagents",
  },
  {
    key: "accreditation",
    name: "CAPTraX — CAP Accreditation",
    icon: ClipboardCheck,
    blurb: "CAP checklist by department with SOP links and evidence, proficiency testing, corrective actions and an inspection-readiness dashboard.",
    to: "/accreditation",
    links: [
      ["Inspection readiness", "/accreditation"], ["Checklist manager", "/accreditation/checklist"],
      ["Proficiency testing", "/accreditation/pt"], ["Nonconformance & CAPA", "/accreditation/nonconformance"],
      ["Frozen sections & IOC", "/accreditation/frozen"],
      ["Report distribution", "/accreditation/distribution"],
      ["Consults & peer review", "/accreditation/consults"],
      ["Retention & disposal", "/accreditation/retention"],
      ["Audit log", "/audit"],
    ],
  },
  {
    key: "interop",
    name: "IT Interop — EHR Integrations",
    icon: PlugZap,
    blurb: "Live simulators for Meditech Expanse (ORM^O01), eClinicalWorks (OML^O21) and FHIR R4 ServiceRequest — send an order and watch it accession.",
    to: "/interop",
    links: [
      ["Meditech Expanse (HL7 ORM)", "/interop?ehr=meditech"],
      ["eClinicalWorks (HL7 OML)", "/interop?ehr=eclinicalworks"],
      ["FHIR R4 ServiceRequest", "/interop?ehr=fhir"],
    ],
  },
  {
    key: "archive",
    name: "ArchivIQ-Med — Record Archive",
    icon: ScanText,
    blurb: "Scan paper requisitions, outside reports and old handwritten logs, have them read by AI and verified, then search them — with a CAP retention clock and destruction certificates.",
    to: "/archive",
    links: [["ArchivIQ-Med", "/archive"], ["Retention & disposal", "/accreditation/retention"]],
  },
  {
    key: "lablync",
    name: "LabLync — Clinical Lab Results",
    icon: Activity,
    blurb: "Read-only CBC and differential from the hospital laboratory, shown beside bone marrow cases so a marrow can be read without leaving PathQity.",
    to: "/lablync",
    perm: "compliance:view",
    links: [["LabLync", "/lablync"], ["All Cases", "/cases"]],
  },
];

const PROPOSAL_FILES = [
  ["Pilot proposal", "16 pages", "PathQity_AP_Pilot_Proposal.pdf", "PathQity_AP_Pilot_Proposal.docx"],
  ["Meeting deck", "10 slides", "PathQity_AP_Pilot_Deck.pdf", "PathQity_AP_Pilot_Deck.pptx"],
  ["IT security summary", "4 pages", "PathQity_IT_Security_Summary.pdf", "PathQity_IT_Security_Summary.docx"],
];

const ProposalsTile = () => (
  <div className="group flex h-full flex-col border border-slate-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#134e4a] hover:shadow-lg"
       data-testid="hub-tile-proposals">
    <div className="flex items-start gap-3">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-[#134e4a]/10 text-[#134e4a] transition-colors group-hover:bg-[#134e4a] group-hover:text-white">
        <Presentation size={20} />
      </span>
      <div className="min-w-0">
        <h2 className="text-base font-semibold leading-snug text-slate-900 md:text-lg">Documents &amp; Proposals</h2>
        <p className="mt-1 text-sm text-slate-600">
          The executive pack for the VP and IT review — pilot proposal, meeting deck and the IT security
          summary. PDF to circulate, Word or PowerPoint to edit.
        </p>
      </div>
      <Download size={18} className="ml-auto shrink-0 text-slate-300 transition-colors group-hover:text-[#134e4a]" />
    </div>
    <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
      {PROPOSAL_FILES.map(([label, meta, pdf, editable]) => (
        <li key={pdf} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-sm">
          <span className="font-medium text-slate-800">{label}</span>
          <span className="text-xs text-slate-400">{meta}</span>
          <span className="ml-auto flex gap-2">
            <a href={`/${pdf}`} download data-testid={`proposal-pdf-${pdf}`}
               className="border border-slate-300 px-2 py-0.5 text-xs font-semibold text-slate-700 hover:border-[#134e4a] hover:text-[#134e4a]">
              PDF
            </a>
            <a href={`/${editable}`} download data-testid={`proposal-src-${editable}`}
               className="border border-slate-300 px-2 py-0.5 text-xs font-semibold text-slate-700 hover:border-[#134e4a] hover:text-[#134e4a]">
              {editable.endsWith(".pptx") ? "PPTX" : "DOCX"}
            </a>
          </span>
        </li>
      ))}
    </ul>
    <p className="mt-2 text-[11px] text-slate-400">
      Screenshots come from this system. Fill in the name, title and date placeholders before sending.
    </p>
  </div>
);

const PathLyncTile = () => (
  <a href="/pathlync" target="_blank" rel="noreferrer" data-testid="hub-tile-pathlync"
     className="group flex h-full flex-col border border-slate-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#1e3a8a] hover:shadow-lg">
    <div className="flex items-start gap-3">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-[#1e3a8a]/10 text-[#1e3a8a] transition-colors group-hover:bg-[#1e3a8a] group-hover:text-white">
        <Microscope size={20} />
      </span>
      <div className="min-w-0">
        <h2 className="text-base font-semibold leading-snug text-slate-900 md:text-lg">PathLync</h2>
        <p className="mt-1 text-sm text-slate-600">
          Remote consultation: send gross and microscopic images to a consulting pathologist and
          get a signed opinion back.
        </p>
      </div>
      <ArrowUpRight size={18} className="ml-auto shrink-0 text-slate-300 transition-all group-hover:-translate-y-0.5 group-hover:text-[#1e3a8a]" />
    </div>
    <p className="mt-4 border-t border-slate-100 pt-3 text-xs font-medium text-slate-500">
      Separate product — opens in a new tab with its own sign-in
    </p>
  </a>
);

const DemoRequestsTile = () => {
  const [counts, setCounts] = useState({ total: 0, open: 0 });
  useEffect(() => {
    api.get("/demo-requests")
      .then(({ data }) => setCounts({
        total: data.length,
        open: data.filter((r) => r.status === "New").length,
      }))
      .catch(() => {});
  }, []);

  return (
    <div className="group flex h-full flex-col border border-slate-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#134e4a] hover:shadow-lg">
      <Link to="/demo-requests" className="flex flex-col" data-testid="hub-tile-demo-requests">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-[#134e4a]/10 text-[#134e4a] transition-colors group-hover:bg-[#134e4a] group-hover:text-white">
            <Mail size={20} />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-snug text-slate-900 md:text-lg">
              Demo Requests &amp; Leads
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Enquiries and pilot-proposal downloads from the public PathQity site. Track status and
              follow-up notes for each one.
            </p>
          </div>
          <ArrowUpRight size={18} className="ml-auto shrink-0 text-slate-300 transition-all group-hover:-translate-y-0.5 group-hover:text-[#134e4a]" />
        </div>
      </Link>
      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 pt-3">
        <span className="text-sm text-slate-600" data-testid="hub-demo-new-count">
          <strong className="font-display text-xl text-[#134e4a]">{counts.open}</strong> new
        </span>
        <span className="text-sm text-slate-600" data-testid="hub-demo-total-count">
          <strong className="font-display text-xl text-slate-800">{counts.total}</strong> total
        </span>
        <a href="/" target="_blank" rel="noreferrer" data-testid="hub-view-public-site"
           className="ml-auto text-xs font-medium text-slate-500 underline-offset-2 hover:text-[#134e4a] hover:underline">
          View the public site
        </a>
      </div>
    </div>
  );
};

const Tile = ({ mod }) => {
  const Icon = mod.icon;
  const locked = mod.soon || !mod.to;
  const head = (
    <>
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-[#134e4a]/10 text-[#134e4a] transition-colors group-hover:bg-[#134e4a] group-hover:text-white">
          <Icon size={20} />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-snug text-slate-900 md:text-lg">{mod.name}</h2>
          <p className="mt-1 text-sm text-slate-600">{mod.blurb}</p>
        </div>
        {locked
          ? <Lock size={16} className="ml-auto shrink-0 text-slate-400" />
          : <ArrowUpRight size={18} className="ml-auto shrink-0 text-slate-300 transition-all group-hover:-translate-y-0.5 group-hover:text-[#134e4a]" />}
      </div>
      {mod.soon && (
        <span className="mt-4 self-start bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-900"
              data-testid={`hub-soon-${mod.key}`}>
          Coming soon
        </span>
      )}
    </>
  );

  const shell = "group flex h-full flex-col border p-5 text-left transition-all";
  if (locked) {
    return (
      <div className={`${shell} border-dashed border-slate-300 bg-slate-50/60`} data-testid={`hub-tile-${mod.key}`}>
        {head}
      </div>
    );
  }
  return (
    <div className={`${shell} border-slate-200 bg-white hover:-translate-y-0.5 hover:border-[#134e4a] hover:shadow-lg`}>
      <Link to={mod.to} className="flex flex-col" data-testid={`hub-tile-${mod.key}`}>{head}</Link>
      {mod.links && (
        <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 border-t border-slate-100 pt-3">
          {mod.links.map(([label, to]) => (
            <Link key={to} to={to} data-testid={`hub-link-${to.replace(/\//g, "") || "root"}`}
                  className="text-xs font-medium text-slate-500 underline-offset-2 hover:text-[#134e4a] hover:underline">
              {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Hub() {
  const { user, can } = useAuth();
  const mods = MODULES.filter((m) => !m.perm || can(m.perm));

  return (
    <div className="space-y-8" data-testid="hub-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">TraX Suite by PathQity</p>
          <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Welcome{user?.name ? `, ${user.name}` : ""}
          </h1>
          <p className="mt-2 max-w-2xl text-base text-slate-600">
            Everything the pathology lab runs on, in one place. Pick a module to get to work.
          </p>
        </div>
      </div>

      <DailyBriefing userId={user?.id} enabled={!!user?.briefing_enabled} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="hub-grid">
        {mods.map((m) => <Tile key={m.key} mod={m} />)}
        <PathLyncTile />
        {can("team:manage") && <DemoRequestsTile />}
        <ProposalsTile />
      </div>
    </div>
  );
}
